import React, { useMemo } from 'react';
import type { Language } from '../../types';
import {
    formatDateTimeLocalPreview,
    joinDateTimeLocalValue,
    splitDateTimeLocalValue,
} from '../../utils/intermittentFasting';

interface FastingDateTimeFieldProps {
    value: string;
    onChange: (value: string) => void;
    language?: Language;
    min?: string;
    max?: string;
    disabled?: boolean;
}

const HOURS = Array.from({ length: 24 }, (_, index) => index);
const MINUTES = Array.from({ length: 60 }, (_, index) => index);

const selectClassName =
    'min-w-0 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-2 py-2 text-sm font-semibold text-slate-800 dark:text-slate-100 shadow-sm focus:outline-none focus:ring-2 focus:ring-teal-500/40';

const FastingDateTimeField: React.FC<FastingDateTimeFieldProps> = ({
    value,
    onChange,
    language,
    min,
    max,
    disabled = false,
}) => {
    const parts = useMemo(() => splitDateTimeLocalValue(value), [value]);
    const preview = useMemo(() => formatDateTimeLocalPreview(value, language), [value, language]);

    const updateParts = (nextDate: string, nextHour: number, nextMinute: number) => {
        onChange(joinDateTimeLocalValue(nextDate, nextHour, nextMinute));
    };

    if (!parts) {
        return (
            <div className="rounded-xl border border-red-200 dark:border-red-900/40 bg-red-50/60 dark:bg-red-900/10 px-3 py-2 text-xs font-semibold text-red-600 dark:text-red-300">
                Invalid date/time
            </div>
        );
    }

    return (
        <div className="space-y-2">
            <input
                type="date"
                value={parts.date}
                min={min ? splitDateTimeLocalValue(min)?.date : undefined}
                max={max ? splitDateTimeLocalValue(max)?.date : undefined}
                disabled={disabled}
                onChange={(event) => updateParts(event.target.value, parts.hour, parts.minute)}
                className={`${selectClassName} w-full`}
            />
            <div className="flex items-center justify-center gap-2" dir="ltr">
                <select
                    value={parts.hour}
                    disabled={disabled}
                    aria-label="Hour"
                    onChange={(event) => updateParts(parts.date, Number(event.target.value), parts.minute)}
                    className={`${selectClassName} w-[4.5rem] text-center`}
                >
                    {HOURS.map((hour) => (
                        <option key={hour} value={hour}>
                            {String(hour).padStart(2, '0')}
                        </option>
                    ))}
                </select>
                <span className="text-sm font-black text-slate-400 dark:text-slate-500" aria-hidden="true">:</span>
                <select
                    value={parts.minute}
                    disabled={disabled}
                    aria-label="Minute"
                    onChange={(event) => updateParts(parts.date, parts.hour, Number(event.target.value))}
                    className={`${selectClassName} w-[4.5rem] text-center`}
                >
                    {MINUTES.map((minute) => (
                        <option key={minute} value={minute}>
                            {String(minute).padStart(2, '0')}
                        </option>
                    ))}
                </select>
            </div>
            {preview && (
                <p className="text-center text-[11px] font-semibold text-slate-400 dark:text-slate-500 tabular-nums">
                    <bdi dir="ltr">{preview}</bdi>
                </p>
            )}
        </div>
    );
};

export default FastingDateTimeField;
